import React, { useState, useEffect, useCallback, useRef } from 'react';
import './ReviewSlider.css';

const ReviewSlider = ({ reviews }) => {
  const [active, setActive] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [animState, setAnimState] = useState('visible'); // 'visible' | 'exit-left' | 'exit-right' | 'enter-left' | 'enter-right'
  const pendingIdx = useRef(null);
  const transitioning = useRef(false);

  const goTo = useCallback((nextIdx, direction = 'next') => {
    if (transitioning.current) return;
    transitioning.current = true;
    pendingIdx.current = nextIdx;

    // Step 1: slide out current card
    setAnimState(direction === 'next' ? 'exit-left' : 'exit-right');

    setTimeout(() => {
      // Step 2: snap to new card off-screen
      setActive(pendingIdx.current);
      setAnimState(direction === 'next' ? 'enter-right' : 'enter-left');

      // Step 3: slide in new card
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimState('visible');
          transitioning.current = false;
        });
      });
    }, 320);
  }, []);

  const next = useCallback(() => {
    const nextIdx = (active + 1) % reviews.length;
    goTo(nextIdx, 'next');
  }, [active, reviews.length, goTo]);

  const prev = useCallback(() => {
    const prevIdx = (active - 1 + reviews.length) % reviews.length;
    goTo(prevIdx, 'prev');
  }, [active, reviews.length, goTo]);

  const jumpTo = useCallback((idx) => {
    if (idx === active || transitioning.current) return;
    goTo(idx, idx > active ? 'next' : 'prev');
  }, [active, goTo]);

  // Auto-slide every 4.5s
  useEffect(() => {
    if (isHovered) return;
    const t = setInterval(next, 4500);
    return () => clearInterval(t);
  }, [next, isHovered]);

  const r = reviews[active];

  const renderStars = (rating) =>
    Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={i < rating ? 'star star--filled' : 'star'}>★</span>
    ));

  return (
    <div
      className="review-slider"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="review-slider__track">
        <div className={`review-slider__card review-slider__card--${animState}`}>
          <div className="review-slider__quote">"</div>
          <p className="review-slider__text">{r.review}</p>
          <div className="review-slider__stars">{renderStars(r.rating)}</div>
          <div className="review-slider__author">
            <div className="review-slider__avatar review-slider__avatar--logo">
              {r.logo
                ? <img src={r.logo} alt={r.company} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '4px' }} />
                : <span style={{ fontFamily: 'var(--f-display)', fontWeight: 700, fontSize: '1.2rem', color: 'var(--red)' }}>{(r.company || 'M').charAt(0).toUpperCase()}</span>
              }
            </div>
            <div>
              <strong className="review-slider__company-main">{r.company}</strong>
              <span className="review-slider__designation">{r.designation}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="review-slider__controls">
        <button className="review-slider__btn" onClick={prev} aria-label="Previous review">
          <svg viewBox="0 0 20 20" fill="none"><path d="M12 15l-5-5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
        </button>
        <div className="review-slider__dots">
          {reviews.map((_, i) => (
            <button
              key={i}
              className={`review-slider__dot ${i === active ? 'review-slider__dot--active' : ''}`}
              onClick={() => jumpTo(i)}
              aria-label={`Review ${i + 1}`}
            />
          ))}
        </div>
        <button className="review-slider__btn" onClick={next} aria-label="Next review">
          <svg viewBox="0 0 20 20" fill="none"><path d="M8 5l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
        </button>
      </div>
    </div>
  );
};

export default ReviewSlider;